import { getCookie } from "@tento-ui/init-core";
import "@tento-ui/brand/brand-app/brand-app";
import "@tento-ui/brand/brand-app/brand-app-content";
import "@tento-ui/brand/header/marketing-header";
import "@tento-ui/brand/landing/landing-footer";
import "@tento-ui/brand/landing/landing-section";
import "@tento-ui/brand/landing/landing-hero";
import "@tento-ui/brand/landing/landing-card";
import "@tento-ui/brand/landing/landing-grid";

import { Tracing } from "@tento-core/tracing";

import { BrandApp } from "@tento-ui/brand/brand-app/brand-app";
import { StickerArtCatalog } from "@tento-ui/components/sticker/sticker-art-catalog";
import { DomUtils } from "@tento-ui/dom";
import { $ } from "@tento-ui/dom-selectors";
import { DarkMode } from "@tento-ui/dark-mode";
import { $$ } from "@tento-ui/fastdom";
import { LazySync } from "@tento-ui/lazy";
import {
  extractNavModel,
  navConfigFromSiteNav,
} from "../lib/brand-app/landing-nav";
import { extractPageModel as extractEnginePageModel } from "../lib/brand-app/page-engine";
import { BrandRouter } from "../lib/brand-app/brand-router";
import { registerBrandDecorators } from "../lib/brand-app/register-decorators";
import {
  publicLandingConfig,
  resolvePublicLandingPageEnginePreset,
} from "../lib/brand-app/landing-public";
import { LonaResources } from "../lib/resources";

const LANDING_LCP_READY_EVENT = "lona:landing-lcp-ready";
const LANDING_LCP_READY_MARK = "lona:landing-lcp-ready";
const LANDING_LCP_IMAGE_PATH = "/@/static/img/landing/feature-home-cards.webp";

// One-shot sidecar load, memoized for the page. `LazySync<Promise<void>>`
// keeps a single in-flight promise so repeat callers share it.
//
// ONE BUNDLE, deliberately. This is a dynamic `import()` of a module in *this*
// bundle, not a second `<script type=module>` root: the client bundler builds
// every `routes/**/*.root.ts` standalone (`bundle: true`, no `splitting`), so a
// second root bundle ships its own copy of the ~10 shared components, and
// `@component` throws by contract when a name is registered twice
// (tento-ui-js/component-decorators.ts) — which aborted the sidecar at
// `std-squircle` before it could reach `LLMLogger.initFromUrlParam()`, taking
// `window.__lonaUxComplete` (and every landing ux-test) with it. With splitting
// off esbuild inlines `import()` as a lazily-initialised module, so the default
// visitor still never evaluates the logger.
//
// DECLARED ABOVE the module's top-level `await` below: the bundler lowers
// `const` to `var`, so a declaration after that await is `undefined` (not a
// TDZ throw) when the awaited path calls `.get()` — which crashed the whole
// landing module at boot for any visitor carrying a ux-test/llm-logger
// cookie (2026-08-05 operator console trace).
const llmLoggerSidecar = new LazySync<Promise<void>>(() =>
  import("./llm-session.root").then(
    () => undefined,
    () => {
      Tracing.warn("ui")("landing UX logger sidecar failed");
    },
  ),
);

await loadLlmLoggerSidecarIfRequested();
installLandingLcpObserver();

StickerArtCatalog.configure({
  loadText: (path) => LonaResources.static.text(path),
  loadBytes: (path) => LonaResources.static.bytes(path),
});

// ─── Extract models from light-DOM (before anything else) ───

const engineRoot = document.querySelector<HTMLElement>("[data-page-engine-root]");
const enginePageModel = extractEnginePageModel(engineRoot);
const navModel = extractNavModel();

const navConfig: BrandApp.NavConfig = navModel
  ? navConfigFromSiteNav(navModel, "current-host")
  : {
      items: [
        { label: "Product" },
        { label: "Solutions" },
        { label: "Resources" },
        { label: "Pricing" },
      ],
      cta: { label: "Get started" },
      // No item carries an href, so nothing in this fallback nav navigates.
      onNavigate: () => {},
    };

// ─── Public landing config ───

const config = publicLandingConfig;
DarkMode.toggle(false, false);

registerBrandDecorators();

// ─── Router ───

const $app = $<BrandApp>("app");
const router = new BrandRouter({
  context: {
    blog: null,
    landing: config.landing.sections,
    onPostNavigate: () => {},
    onBlogBack: () => {},
  },
  initialPageModel: enginePageModel,
  fallback: () => document.createElement("div"),
  resolvePreset: resolvePublicLandingPageEnginePreset,
});

router.register((resolve) => renderRoute(resolve), [
  { pattern: "/for/{slug}", path: ([slug]) => `/for/${slug}` },
  { pattern: "/features/{slug}", path: ([slug]) => `/features/${slug}` },
  { pattern: "/solutions/{slug}", path: ([slug]) => `/solutions/${slug}` },
  { pattern: "/jobs/{slug}", path: ([slug]) => `/jobs/${slug}` },
]);
await renderRoute(router.initialResolve(window.location.pathname));

await document.fonts.ready;
await waitForInitialLandingLayout();
await DomUtils.hydrateAndReveal();

async function renderRoute(resolve: BrandApp.Config["resolve"]) {
  await $app.render({
    ds: config.brandDS,
    pageStyle: config.landing.style,
    navConfig,
    resolve,
  });
}

async function loadLlmLoggerSidecarIfRequested(): Promise<void> {
  if (!shouldLoadLlmLoggerSidecar()) return;
  await loadLlmLoggerSidecar();
}

function shouldLoadLlmLoggerSidecar(): boolean {
  const params = new URLSearchParams(window.location.search);
  if (params.get("ux") != null) return true;
  if (getCookie("lona-llm-logger") === "1") return true;
  if (getCookie("ux-test-session") != null) return true;

  try {
    return localStorage.getItem("lona:llm-logger") === "1";
  } catch {
    return false;
  }
}


function loadLlmLoggerSidecar(): Promise<void> {
  return llmLoggerSidecar.get();
}

async function waitForInitialLandingLayout(): Promise<void> {
  const requiredStableFrames = 2;
  const maxFrames = 12;
  let previousHeight = -1;
  let stableFrames = 0;

  for (let frame = 0; frame < maxFrames; frame++) {
    const height = await measureBodyHeight();
    if (height > 0 && height === previousHeight) {
      stableFrames += 1;
      if (stableFrames >= requiredStableFrames) return;
    } else {
      previousHeight = height;
      stableFrames = 0;
    }
  }
}

// The height gate is a layout READ, so it belongs in fastdom's read phase
// (`$$.measure`) — not `$$.mutate` (the write phase) or a raw rAF that would
// force a sync `getBoundingClientRect` outside the batch and thrash against the
// hydration-reveal writes flushed the same frame. Each `measure` schedules the
// next rAF batch, so the loop still advances one frame per iteration.
function measureBodyHeight(): Promise<number> {
  return new Promise((resolve) => {
    $$.measure(() =>
      resolve(Math.round(document.body.getBoundingClientRect().height)),
    );
  });
}

function installLandingLcpObserver(): void {
  if (
    typeof PerformanceObserver === "undefined" ||
    !PerformanceObserver.supportedEntryTypes.includes("largest-contentful-paint")
  ) {
    return;
  }

  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      const lcp = entry as LargestContentfulPaint;
      if (!isLandingLcpImage(lcp)) continue;
      observer.disconnect();
      $$.mutate(markLandingLcpReady);
      return;
    }
  });
  observer.observe({ type: "largest-contentful-paint", buffered: true });
}

function isLandingLcpImage(entry: LargestContentfulPaint): boolean {
  return entry.url.endsWith(LANDING_LCP_IMAGE_PATH);
}

function markLandingLcpReady(): void {
  if (performance.getEntriesByName(LANDING_LCP_READY_MARK).length === 0) {
    performance.mark(LANDING_LCP_READY_MARK);
  }
  window.dispatchEvent(new Event(LANDING_LCP_READY_EVENT));
}
